import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Label, FormGroup as RSFormGroup } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../../components/custom/BackButton';
import { createdUpdatedInfo, getTsDate } from '../../../../utils';
import ActionIcon from '../../../../components/custom/ActionIcon';
import { SelectOption, IUser, JobTitle } from '../../../../globals/interfaces';
import useTypedForm from '../../../../components/custom/hookForm/useTypedForm';
import { createSchema, defaultValues, updateSchema } from './schema';
import Select from '../../../../components/Select';
import { yupResolver } from '@hookform/resolvers/yup';
import FormError from '../../../../components/custom/FormError2';
import { DEPARTMENTS } from '../../../../components/hr/constants';

const departmentOptions: Array<SelectOption> = DEPARTMENTS.map(item => ({ label: item, value: item }));

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    updateItem: JobTitle | undefined;
    user: IUser;
    handleCreateUpdateJobTitle: (data: JobTitle) => Promise<void>;
    launchDeleteJob: () => void;
}

export default function ModalJobTitles ({
    modal,
    setModal,
    updateItem,
    user,
    handleCreateUpdateJobTitle,
    launchDeleteJob
}: Props) {

    const update = Boolean(updateItem);
    const useSchema = update ? updateSchema : createSchema;
    const {
        formState: {
            errors,
            isValid,
            isDirty
        },
        watch,
        setValue,
        handleSubmit,
        trigger,
        reset,
        FormInput
    } = useTypedForm<JobTitle>({
        mode: 'all',
        resolver: yupResolver(useSchema)
    });

    const department = watch('department');

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (updateItem) {
            reset({
                ...updateItem,
                modifiedAt: getTsDate(),
                modifiedBy: user.s_email
            });
        } else {
            reset({
                ...defaultValues,
                createdAt: getTsDate(),
                createdBy: user.s_email,
                modifiedAt: getTsDate(),
                modifiedBy: user.s_email
            });
        }
        trigger();
    }, [modal, updateItem, user]);

    const handleSelectDepartment = (value) => {
        setValue('department', value, {
            shouldDirty: true,
            shouldValidate: true
        });
    }

    const submitHandler = async () => {
        setLoading(true);
        await handleSubmit(handleCreateUpdateJobTitle)();
        setLoading(false);
    }

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} Job</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormInput 
                    label={'Job ID'}
                    name={'jobId'}
                />
                <RSFormGroup>
                    <Label>Department <FormError name={'department'} errors={errors} /></Label>
                    <Select 
                        options={departmentOptions}
                        value={department}
                        onChange={(option) => handleSelectDepartment(option.value)}
                    />
                </RSFormGroup>
                <FormInput 
                    label={'Job Title'}
                    name={'title'}
                />
                <FormInput 
                    label={'Description'}
                    name={'description'}
                    type={'textarea'}
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo(updateItem)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon 
                                    type={'delete'}
                                    onClick={() => launchDeleteJob()}
                                />
                            )
                        }
                        <ActionIcon 
                            type={'save'} 
                            onClick={() => submitHandler()}
                            disabled={!isValid || !isDirty} 
                            loading={loading}
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{update?: boolean}>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;